import { useEffect, useState } from 'react';
import { useAdminData } from '../../admin/AdminDataContext';
import type { LayerGroup, LayerGroupVisibilityMode } from '../../admin/types';
import { ApiError } from '../../lib/api';
import { layersApi, type LayerDto } from '../../lib/layersApi';

const colorPalette = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#06b6d4', '#475569', '#db2777'];

const emptyForm: Omit<LayerGroup, 'id' | 'sortOrder'> = {
  name: '',
  color: colorPalette[0],
  parentId: null,
  defaultExpanded: true,
  defaultVisible: true,
  visibilityMode: 'independent',
  minZoom: null,
  maxZoom: null,
};

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`flex h-[26px] w-[52px] shrink-0 items-center rounded-full px-1 transition-colors ${
        checked ? 'bg-action-primary justify-end' : 'bg-bg-surface-secondary justify-start'
      }`}
    >
      <span className="size-[18px] rounded-full bg-bg-surface shadow" />
    </button>
  );
}

export function LayerGroupsTab() {
  const { layerGroups, addLayerGroup, updateLayerGroup, deleteLayerGroup, moveLayerGroup, mapSettings } = useAdminData();
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Omit<LayerGroup, 'id' | 'sortOrder'>>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<LayerGroup | null>(null);

  const [realLayers, setRealLayers] = useState<LayerDto[]>([]);
  const [assignedLayerIds, setAssignedLayerIds] = useState<Set<string>>(new Set());
  const [originalAssignedIds, setOriginalAssignedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapSettings?.id) return;
    layersApi
      .listLayers(mapSettings.id)
      .then(setRealLayers)
      .catch(() => setRealLayers([]));
  }, [mapSettings?.id]);

  const layerCountFor = (name: string) => realLayers.filter((l) => l.group === name).length;

  const roots = layerGroups.filter((g) => g.parentId === null).sort((a, b) => a.sortOrder - b.sortOrder);
  const childrenOf = (parentId: string) =>
    layerGroups.filter((g) => g.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder);

  const startCreate = () => {
    setForm(emptyForm);
    setEditingId('new');
    setAssignedLayerIds(new Set());
    setOriginalAssignedIds(new Set());
    setSaveError(null);
  };

  const startEdit = (group: LayerGroup) => {
    const { id: _id, sortOrder: _sortOrder, ...rest } = group;
    setForm(rest);
    setEditingId(group.id);
    const current = new Set(realLayers.filter((l) => l.group === group.name).map((l) => l.id));
    setAssignedLayerIds(current);
    setOriginalAssignedIds(new Set(current));
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setAssignedLayerIds(new Set());
    setOriginalAssignedIds(new Set());
    setSaveError(null);
  };

  const toggleLayerAssigned = (layerId: string) => {
    setAssignedLayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) next.delete(layerId);
      else next.add(layerId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const targetName = form.name.trim();
      if (editingId === 'new') {
        await addLayerGroup(form);
      } else if (editingId) {
        await updateLayerGroup(editingId, form);
      }

      const toUpdate: { id: string; group: string | null }[] = [];
      for (const layer of realLayers) {
        const wasAssigned = originalAssignedIds.has(layer.id);
        const nowAssigned = assignedLayerIds.has(layer.id);
        if (nowAssigned) {
          if (layer.group !== targetName) toUpdate.push({ id: layer.id, group: targetName });
        } else if (wasAssigned) {
          toUpdate.push({ id: layer.id, group: null });
        }
      }

      if (toUpdate.length > 0) {
        await Promise.all(toUpdate.map((u) => layersApi.setLayerGroup(u.id, u.group)));
        if (mapSettings?.id) setRealLayers(await layersApi.listLayers(mapSettings.id));
      }

      cancelEdit();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить состав группы.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteLayerGroup(deleteTarget.id).catch(() => {});
    if (editingId === deleteTarget.id) cancelEdit();
    setDeleteTarget(null);
  };

  const renderGroup = (group: LayerGroup, depth: number) => {
    const children = childrenOf(group.id);
    const siblings = layerGroups.filter((g) => g.parentId === group.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
    const position = siblings.findIndex((g) => g.id === group.id);
    const count = layerCountFor(group.name);

    return (
      <div key={group.id}>
        <div
          className="flex items-center gap-3 rounded-xl border border-border-default bg-bg-surface p-3"
          style={{ marginLeft: depth * 28 }}
        >
          <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-text-primary">{group.name}</p>
            <p className="text-[11px] text-text-secondary">{count > 0 ? `${count} слоёв` : 'Слоёв нет'}</p>
          </div>

          {group.visibilityMode === 'exclusive' && (
            <span className="shrink-0 rounded-lg bg-ai-accent px-2 py-1 text-[10px] font-medium text-text-inverse">
              Взаимоисключающая
            </span>
          )}
          {!group.defaultVisible && (
            <span className="shrink-0 rounded-lg bg-bg-surface-secondary px-2 py-1 text-[10px] font-medium text-text-secondary">
              Скрыта по умолчанию
            </span>
          )}
          {(group.minZoom != null || group.maxZoom != null) && (
            <span className="shrink-0 rounded-lg bg-bg-surface-secondary px-2 py-1 text-[10px] font-medium text-text-secondary">
              z {group.minZoom ?? 0}–{group.maxZoom ?? 20}
            </span>
          )}

          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={position <= 0}
              onClick={() => moveLayerGroup(group.id, 'up').catch(() => {})}
              title="Выше"
              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              disabled={position >= siblings.length - 1}
              onClick={() => moveLayerGroup(group.id, 'down').catch(() => {})}
              title="Ниже"
              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-30"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => startEdit(group)}
              title="Редактировать"
              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary"
            >
              ✎
            </button>
            <button
              type="button"
              onClick={() => setDeleteTarget(group)}
              title="Удалить"
              className="flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-status-danger/10 hover:text-status-danger"
            >
              ✕
            </button>
          </div>
        </div>

        {children.length > 0 && (
          <div className="mt-2 flex flex-col gap-2">{children.map((child) => renderGroup(child, depth + 1))}</div>
        )}
      </div>
    );
  };

  const otherGroupsForParent = layerGroups.filter((g) => g.id !== editingId);

  return (
    <div className="grid grid-cols-[1fr_420px] gap-6 px-8 py-6">
      <section className="flex flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[15px] font-semibold text-text-primary">Группы слоёв</h2>
            <p className="mt-1 text-xs text-text-secondary">
              {realLayers.length === 0
                ? 'Слоёв в этом пространстве пока нет — группы можно подготовить заранее, чтобы сразу распределить данные по категориям, когда они появятся через «+ Добавить слой».'
                : 'Состав группы (какие слои в неё входят) назначается в панели справа — откройте группу на редактирование.'}
            </p>
          </div>
          <button
            type="button"
            onClick={startCreate}
            className="flex h-10 shrink-0 items-center rounded-[10px] bg-action-primary px-4 text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover"
          >
            + Добавить группу
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          {roots.length === 0 ? (
            <div className="rounded-xl bg-bg-surface-secondary p-6 text-center">
              <p className="text-[13px] font-medium text-text-primary">Групп ещё нет</p>
              <p className="mt-1 text-[11px] text-text-secondary">Нажмите «+ Добавить группу», чтобы создать первую</p>
            </div>
          ) : (
            roots.map((group) => renderGroup(group, 0))
          )}
        </div>
      </section>

      {editingId && (
        <section className="flex h-fit flex-col rounded-2xl border border-border-default bg-bg-surface p-6">
          <h2 className="text-[15px] font-semibold text-text-primary">
            {editingId === 'new' ? 'Новая группа' : 'Редактирование группы'}
          </h2>

          <label className="mt-4 text-[11px] font-medium text-text-secondary">Название группы</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Например, Инженерная инфраструктура"
            className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
          />

          <label className="mt-3 text-[11px] font-medium text-text-secondary">Родительская группа</label>
          <select
            value={form.parentId ?? ''}
            onChange={(e) => setForm({ ...form, parentId: e.target.value || null })}
            className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
          >
            <option value="">Без родителя</option>
            {otherGroupsForParent.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>

          <label className="mt-3 text-[11px] font-medium text-text-secondary">Цвет</label>
          <div className="mt-1.5 flex gap-2">
            {colorPalette.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setForm({ ...form, color })}
                title={color}
                className={`size-7 rounded-full border-2 ${form.color === color ? 'border-text-primary' : 'border-transparent'}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          <label className="mt-3 text-[11px] font-medium text-text-secondary">Режим видимости</label>
          <select
            value={form.visibilityMode}
            onChange={(e) => setForm({ ...form, visibilityMode: e.target.value as LayerGroupVisibilityMode })}
            className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none focus:border-action-primary"
          >
            <option value="independent">Независимая — слои переключаются по отдельности</option>
            <option value="exclusive">Взаимоисключающая — виден только один слой из группы</option>
          </select>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="flex flex-col">
              <label className="text-[11px] font-medium text-text-secondary">Мин. масштаб (zoom)</label>
              <input
                type="number"
                value={form.minZoom ?? ''}
                onChange={(e) => setForm({ ...form, minZoom: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="Без ограничений"
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[11px] font-medium text-text-secondary">Макс. масштаб (zoom)</label>
              <input
                type="number"
                value={form.maxZoom ?? ''}
                onChange={(e) => setForm({ ...form, maxZoom: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="Без ограничений"
                className="mt-1.5 h-10 rounded-[9px] border border-border-default bg-bg-surface px-3 text-[11px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
              />
            </div>
          </div>

          <div className="my-4 h-px bg-border-default" />

          <div className="flex items-center justify-between gap-4">
            <span className="text-[11px] font-medium text-text-secondary">Развёрнута по умолчанию</span>
            <ToggleSwitch checked={form.defaultExpanded} onChange={() => setForm({ ...form, defaultExpanded: !form.defaultExpanded })} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-4">
            <span className="text-[11px] font-medium text-text-secondary">Видима по умолчанию</span>
            <ToggleSwitch checked={form.defaultVisible} onChange={() => setForm({ ...form, defaultVisible: !form.defaultVisible })} />
          </div>

          <div className="my-4 h-px bg-border-default" />

          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium text-text-secondary">Слои в группе</p>
            <span className="text-[11px] font-medium text-text-primary">{assignedLayerIds.size}</span>
          </div>
          {realLayers.length === 0 ? (
            <p className="mt-2 text-[11px] text-text-secondary">
              Слоёв ещё нет — сначала загрузите их через «Данные и слои → + Добавить данные»
            </p>
          ) : (
            <div className="mt-2 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border border-border-default p-1.5">
              {realLayers.map((layer) => (
                <label
                  key={layer.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-bg-surface-secondary"
                >
                  <input
                    type="checkbox"
                    checked={assignedLayerIds.has(layer.id)}
                    onChange={() => toggleLayerAssigned(layer.id)}
                    className="size-[16px] shrink-0 rounded-[4px] accent-action-primary"
                  />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-text-primary">{layer.name}</span>
                  {layer.group && layer.group !== form.name.trim() && (
                    <span className="shrink-0 text-[10px] text-text-secondary">из «{layer.group}»</span>
                  )}
                </label>
              ))}
            </div>
          )}

          {saveError && <p className="mt-3 text-xs font-medium text-status-danger">{saveError}</p>}

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={cancelEdit}
              className="flex h-10 flex-1 items-center justify-center rounded-[10px] border border-border-default bg-bg-surface text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!form.name.trim() || saving}
              className="flex h-10 flex-1 items-center justify-center rounded-[10px] bg-action-primary text-[13px] font-medium text-text-inverse hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Сохраняем…' : editingId === 'new' ? 'Создать' : 'Сохранить'}
            </button>
          </div>
        </section>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-4">
          <div className="w-full max-w-[440px] rounded-[18px] border border-border-default bg-bg-surface p-6">
            <h3 className="text-base font-semibold text-text-primary">Удалить группу «{deleteTarget.name}»?</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Вложенные подгруппы будут удалены вместе с ней. Слои из группы не удаляются — они просто останутся без
              группы.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex h-10 items-center rounded-md border border-border-strong bg-bg-surface px-4 text-sm font-medium text-text-primary hover:bg-bg-surface-secondary"
              >
                Нет
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex h-10 items-center rounded-md bg-status-danger px-4 text-sm font-medium text-text-inverse hover:opacity-90"
              >
                Да, удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
